import { Checkbox, FormControlLabel, FormGroup, IconButton, Link } from '@mui/material';
import React from 'react';
import { ReactComponent as PanelCollapseIcon } from '../../../../assets/panel-collapse.svg';
import { TopLevelItem, TripDivider, TripList } from '../../../../redux/trip/trip.types';
import { getElementByIdInArray } from '../../../../redux/trip/trip.utils';
import './TripMapFilter.scss';

interface AvailableDay {
  dateISO: string;
  label: string;
  hasEvents: boolean;
}

interface TripMapFilterProps {
  selectedLists: TripList[];
  listsWithCards: TripList[];
  orderedTopLevelItems: TopLevelItem[];
  handleUnselectAll: () => void;
  handleSelectAll: () => void;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDividerToggle: (divider: TripDivider) => void;
  onClose?: () => void;
  availableDays: AvailableDay[];
  selectedDayDateISOs: string[];
  handleDayToggle: (dateISO: string) => void;
  handleSelectAllDays: () => void;
  handleUnselectAllDays: () => void;
}

function TripMapFilter({
  selectedLists,
  listsWithCards,
  orderedTopLevelItems,
  handleUnselectAll,
  handleSelectAll,
  handleChange,
  handleDividerToggle,
  onClose,
  availableDays,
  selectedDayDateISOs,
  handleDayToggle,
  handleSelectAllDays,
  handleUnselectAllDays,
}: TripMapFilterProps) {
  // Drives "Select All" / "Unselect All" label toggle for the Lists section
  const allListsSelected = selectedLists.length === listsWithCards.length;
  // Days that actually have itinerary events — days without events are shown disabled and excluded from Select All
  const daysWithEvents = availableDays.filter((d) => d.hasEvents);
  const allDaysSelected =
    daysWithEvents.length > 0 && selectedDayDateISOs.length === daysWithEvents.length;

  // Shared renderer for both top-level lists and divider-nested lists (className controls indent)
  function renderList(list: TripList, className?: string) {
    return (
      <FormControlLabel
        className={`trip-map-form-control-label${className ? ` ${className}` : ''}`}
        key={list.id}
        control={
          <Checkbox
            id={list.id}
            checked={getElementByIdInArray(selectedLists, list.id) !== undefined}
            onChange={handleChange}
            style={{ alignItems: 'flex-start' }}
          />
        }
        label={
          <div className='trip-map-filter-item-container'>
            <div className='trip-map-filter-item-emoji'>{list.emoji}</div>
            <div>{list.name}</div>
          </div>
        }
      />
    );
  }

  function renderDivider(divider: TripDivider) {
    // Filter through listsWithCards rather than divider.listOrder directly — only show lists
    // that have map markers, preserving the divider's internal ordering
    const dividerListsWithCards = divider.listOrder
      .map((id) => listsWithCards.find((l) => l.id === id))
      .filter((l): l is TripList => l !== undefined);
    if (dividerListsWithCards.length === 0) {
      return null;
    }

    const selectedCount = dividerListsWithCards.filter((l) =>
      selectedLists.some((s) => s.id === l.id)
    ).length;
    const allDividerSelected = selectedCount === dividerListsWithCards.length;
    // someDividerSelected drives the MUI indeterminate state (dash) on the divider checkbox
    const someDividerSelected = selectedCount > 0 && !allDividerSelected;

    return (
      <React.Fragment key={divider.id}>
        <FormControlLabel
          className='trip-map-form-control-label trip-map-filter-divider-label'
          control={
            <Checkbox
              checked={allDividerSelected}
              indeterminate={someDividerSelected}
              onChange={() => handleDividerToggle(divider)}
              style={{ alignItems: 'flex-start' }}
            />
          }
          label={
            <div className='trip-map-filter-item-container'>
              <div className='trip-map-filter-item-emoji'>{divider.emoji}</div>
              <div>{divider.name}</div>
            </div>
          }
        />
        {dividerListsWithCards.map((list) => renderList(list, 'trip-map-filter-nested-list-label'))}
      </React.Fragment>
    );
  }

  return (
    <div className='trip-map-filter'>
      <div className='trip-map-filter-header'>
        <h4 className='trip-map-filter-heading'>Filter</h4>
        {onClose && (
          <IconButton
            size='small'
            onClick={onClose}
          >
            <PanelCollapseIcon />
          </IconButton>
        )}
      </div>

      {listsWithCards.length > 0 && (
        <div className='trip-map-filter-section'>
          <div className='trip-map-filter-section-header'>
            <span className='trip-map-filter-section-label'>Lists</span>
            <Link onClick={allListsSelected ? handleUnselectAll : handleSelectAll}>
              {allListsSelected ? 'Unselect All' : 'Select All'}
            </Link>
          </div>
          <div className='trip-map-filter-section-content'>
            <FormGroup>
              {/* Render in sidebar order; top-level lists skip if they have no cards */}
              {orderedTopLevelItems.map((item) => {
                if (item.itemType === 'list') {
                  const list = listsWithCards.find((l) => l.id === item.data.id);
                  return list ? renderList(list) : null;
                }
                return renderDivider(item.data);
              })}
            </FormGroup>
          </div>
        </div>
      )}

      <div className='trip-map-filter-divider' />

      <div className='trip-map-filter-section'>
        <div className='trip-map-filter-section-header'>
          <span className='trip-map-filter-section-label'>Itinerary</span>
          {daysWithEvents.length > 0 && (
            <Link onClick={allDaysSelected ? handleUnselectAllDays : handleSelectAllDays}>
              {allDaysSelected ? 'Unselect All' : 'Select All'}
            </Link>
          )}
        </div>

        {availableDays.length === 0 ? (
          <p className='trip-map-filter-empty'>
            Set trip dates in your itinerary to visualize days on the map.
          </p>
        ) : (
          <div className='trip-map-filter-section-content'>
            <FormGroup>
              {availableDays.map(({ dateISO, label, hasEvents }) => (
                <div key={dateISO}>
                  <FormControlLabel
                    className='trip-map-form-control-label'
                    control={
                      <Checkbox
                        checked={hasEvents && selectedDayDateISOs.includes(dateISO)}
                        onChange={() => handleDayToggle(dateISO)}
                        disabled={!hasEvents}
                        style={{ alignItems: 'flex-start' }}
                      />
                    }
                    label={label}
                  />
                  {!hasEvents && (
                    <p className='trip-map-filter-day-empty'>
                      Nothing scheduled on your itinerary.
                    </p>
                  )}
                </div>
              ))}
            </FormGroup>
          </div>
        )}
      </div>
    </div>
  );
}

TripMapFilter.displayName = 'TripMapFilter';
export default TripMapFilter;
